/**
 * StepPicker — catalog step-template picker for the process editor
 * Offers "Blank step" and "Dynamic step" plus catalog step
 * templates (system first, then account customs — ordering from
 * useStepTemplates). Picking a template pre-fills the inserted step's
 * config; picking Dynamic inserts a runtime-planned step.
 */

import type { StepConfig } from '@localagent/sdk';
import { useStepTemplates } from '../../hooks/useAgentProcesses';
import type { EditorStepNode } from './process-graph';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const BLANK_STEP: StepConfig = { task_prompt: '' };

interface StepPickerProps {
  /** Called with the picked node (a clone — safe to mutate in editor state). */
  onPick: (node: EditorStepNode) => void;
  onCancel: () => void;
}

export function StepPicker({ onPick, onCancel }: StepPickerProps) {
  const { data: templates, isLoading } = useStepTemplates();

  return (
    <div data-testid="step-picker" className="rounded-md bg-muted p-2 space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-caption font-medium text-muted-foreground">Add step from…</span>
        <button
          type="button"
          data-testid="step-picker-cancel"
          onClick={onCancel}
          className="text-caption text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      <button
        type="button"
        data-testid="step-picker-blank"
        onClick={() => onPick({ kind: 'agent', label: '', config: { ...BLANK_STEP } })}
        className="block w-full rounded px-2 py-1.5 text-left text-caption hover:bg-background"
      >
        <span className="font-medium">Blank step</span>
        <span className="ml-2 text-muted-foreground">start from scratch</span>
      </button>
      <button
        type="button"
        data-testid="step-picker-dynamic"
        onClick={() => onPick({ kind: 'dynamic', label: '', taskPrompt: '' })}
        className="block w-full rounded px-2 py-1.5 text-left text-caption hover:bg-background"
      >
        <span className="font-medium">Dynamic step</span>
        <span className="ml-2 text-muted-foreground">sub-steps planned at run time</span>
      </button>
      {isLoading && <LoadingState variant="inline" label="Loading templates…" className="px-2 py-1" />}
      {templates?.map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid={`step-picker-template-${t.id}`}
          onClick={() => onPick({ kind: 'agent', label: t.name, config: structuredClone(t.step) })}
          className="block w-full rounded px-2 py-1.5 text-left text-caption hover:bg-background"
        >
          <span className="font-medium">{t.name}</span>
          {t.is_system && (
            <span className="ml-2 rounded bg-background px-1 py-0.5 text-micro text-muted-foreground">catalog</span>
          )}
          {t.description && <span className="ml-2 text-muted-foreground">{t.description}</span>}
        </button>
      ))}
    </div>
  );
}
